/**
 * Custom Material DateAdapter that pins parse/format to dd/MM/yyyy
 * regardless of MAT_DATE_LOCALE. Calendar month/weekday labels still localize
 * via the parent NativeDateAdapter.
 */

import { Inject, Injectable } from '@angular/core';
import { MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { Platform } from '@angular/cdk/platform';
import { parseDate } from './date.utils';

const DD_MM_YYYY_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

@Injectable()
export class DdMmYyyyDateAdapter extends NativeDateAdapter {
  constructor(
    @Inject(MAT_DATE_LOCALE) matDateLocale: string,
    platform: Platform,
  ) {
    super(matDateLocale, platform);
  }

  override parse(value: unknown): Date | null {
    if (value == null || value === '') return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      const match = DD_MM_YYYY_REGEX.exec(trimmed);
      if (match) {
        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);
        if (month < 1 || month > 12 || day < 1 || day > 31) return null;
        const d = new Date(year, month - 1, day);
        if (
          d.getFullYear() !== year ||
          d.getMonth() !== month - 1 ||
          d.getDate() !== day
        ) {
          return null;
        }
        return d;
      }
      return parseDate(trimmed);
    }
    return parseDate(value);
  }

  override format(date: Date, displayFormat: Object): string {
    if (!date || isNaN(date.getTime())) return '';

    if (typeof displayFormat === 'string') {
      if (displayFormat.includes('d') && displayFormat.includes('M') && displayFormat.includes('y')) {
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
      }
      if (displayFormat === 'MMMM yyyy') {
        return super.format(date, { month: 'long', year: 'numeric' });
      }
      if (displayFormat === 'MMM yyyy') {
        return super.format(date, { month: 'short', year: 'numeric' });
      }
    }

    if (typeof displayFormat === 'object' && displayFormat !== null) {
      const opts = displayFormat as Intl.DateTimeFormatOptions;
      if (opts.day && opts.month && opts.year && !opts.hour) {
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
      }
    }

    return super.format(date, displayFormat);
  }
}
